import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt, fmt2, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Store, BarChart3, Eye, Star, DollarSign, Calendar, Download, Mail } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function VendorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, b, p] = await Promise.all([
          api.get("/vendor/stats"),
          api.get("/vendor/bookings"),
          api.get("/vendor/profile"),
        ]);
        setStats(s.data); setBookings(b.data); setProfile(p.data);
      } catch (error) {
        console.error("Failed to load vendor dashboard data:", error);
        toast.error("Failed to load dashboard data. Please try again.");
      }
    };
    load();
  }, []);

  if (!user) return null;
  if (user.role !== "vendor" && !profile?.business_name) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-14 text-center">
        <h1 className="font-display text-3xl font-semibold">You&apos;re almost there</h1>
        <p className="text-muted-foreground mt-2">Complete your vendor profile to access the dashboard.</p>
        <Link to="/vendor/onboarding"><Button className="mt-5 rounded-xl" data-testid="start-onboarding-button">Start vendor onboarding</Button></Link>
      </div>
    );
  }

  const kpi = (label, value, Icon, testid) => (
    <div className="rounded-2xl bg-card border border-border p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center"><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">{value}</div>
    </div>
  );

  const totals = stats?.totals || {};
  const monthly = stats?.monthly || [];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-10" data-testid="vendor-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Vendor dashboard</h1>
          <p className="text-muted-foreground">Welcome back, {profile?.business_name || user?.name}. Here&apos;s how your offerings are performing.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`${API_BASE}/exports/vendor/bookings.csv`} target="_blank" rel="noreferrer">
            <Button variant="secondary" className="rounded-xl" data-testid="vendor-export-csv"><Download className="h-4 w-4 mr-2" /> Export bookings (CSV)</Button>
          </a>
          <Select onValueChange={async (v) => { try { await api.post("/exports/schedule", { frequency: v }); toast.success(v === "off" ? "Email reports disabled" : `Email reports set to ${v}`); } catch (error) { console.error("Failed to schedule reports:", error); toast.error("Could not schedule reports"); } }}>
            <SelectTrigger className="rounded-xl w-[180px]" data-testid="vendor-schedule-reports"><Mail className="h-4 w-4 mr-2" /> <SelectValue placeholder="Email reports" /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="off">Off</SelectItem>
            </SelectContent>
          </Select>
          <Link to="/vendor/listings"><Button variant="secondary" className="rounded-xl" data-testid="go-to-listings-button"><Store className="h-4 w-4 mr-2" /> Manage listings</Button></Link>
          <Link to="/vendor/bookings"><Button className="rounded-xl" data-testid="go-to-bookings-button">View bookings <ArrowUpRight className="h-4 w-4 ml-2" /></Button></Link>
        </div>
      </div>

      <section className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
        {kpi("Listings", totals.listings || 0, Store, "kpi-listings")}
        {kpi("Total views", totals.views || 0, Eye, "kpi-views")}
        {kpi("Bookings", totals.bookings || 0, Calendar, "kpi-bookings")}
        {kpi("Revenue (incl. fee)", fmt2(totals.revenue || 0), DollarSign, "kpi-revenue")}
        {kpi("Avg rating", (totals.avg_rating ?? 0).toFixed(1), Star, "kpi-rating")}
      </section>

      <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl bg-card border border-border p-5" data-testid="vendor-trend-chart-card">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Performance over time</h2>
            <span className="text-xs text-muted-foreground">Last 6 months</span>
          </div>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="iv-chart" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} fontSize={12} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--chart-1))" strokeWidth={2.5} fill="url(#iv-chart)" name="Revenue" />
                <Area type="monotone" dataKey="bookings" stroke="hsl(var(--chart-2))" strokeWidth={2} fill="transparent" name="Bookings" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-card border border-border p-5" data-testid="vendor-top-listings-card">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Top listings</h2>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="mt-3 grid gap-2">
            {(stats?.top_listings || []).length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
            {(stats?.top_listings || []).map((l) => (
              <Link to={`/listings/${l.listing_id}`} key={l.listing_id} className="rounded-xl border border-border p-3 hover:bg-muted transition-colors" data-testid="top-listing-row">
                <div className="text-sm font-semibold line-clamp-1">{l.title}</div>
                <div className="text-xs text-muted-foreground mt-1">{l.bookings} bookings · {l.views} views · <span className="text-foreground font-medium">{fmt2(l.revenue)}</span></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-2xl bg-card border border-border p-5" data-testid="vendor-recent-bookings-card">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Recent bookings</h2>
          <Link to="/vendor/bookings" className="text-sm text-[hsl(var(--primary))] font-medium">View all →</Link>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-left"><tr><th className="py-2">Trip</th><th>Date</th><th>Guests</th><th>Contact</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {bookings.slice(0, 6).map((b) => (
                <tr key={b.booking_id} className="border-t border-border">
                  <td className="py-2 pr-2 max-w-xs truncate"><Link to={`/listings/${b.listing_id}`} className="font-medium">{b.title}</Link></td>
                  <td>{b.date}</td>
                  <td>{b.guests}</td>
                  <td className="truncate max-w-[200px]">{b.contact_name} · {b.contact_email}</td>
                  <td className="text-right font-medium">{fmt2(b.line_final)}</td>
                </tr>
              ))}
              {bookings.length === 0 && (<tr><td colSpan={5} className="py-5 text-center text-muted-foreground">No bookings yet</td></tr>)}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
